// Online 1v1 message shapes, shared by the game server and the browser. Types and validation
// only: no network code lives in the rules package.
import { ITEMS, type Item } from './items.js';
import type { MazeWire } from './maze.js';
import type { Action, AgentView, InputRejection, MatchEvent, MatchResult, Mirror } from './match.js';
import { ROTATIONS, type Rotation } from './orientation.js';

/**
 * Every id a client receives is from its own point of view: `"player"` is always the receiving
 * player and `"rival"` the opponent, whichever seat they hold on the server.
 */
export const SELF_ID = 'player';
export const RIVAL_ID = 'rival';

/** Server → client. */
export interface ServerMessages {
  /** Waiting for an opponent. `code` is the private room code to share, if any. */
  waiting: { code: string | null };
  /** An opponent is here: the maze (without its secrets) and when the match starts. */
  ready: { maze: MazeWire; startsInMs: number; round: number; rivalArmor: ArmorChoice; mirrors: Mirror[] };
  /** The player's own view at server match time `t`, plus the events they're allowed to see. */
  state: { t: number; view: AgentView; events: MatchEvent[] };
  /** An input was refused (e.g. pressed during a lock that the client didn't know about yet). */
  rejected: { reason: InputRejection | 'notStarted' | 'badInput' };
  /** The match ended. Items are revealed only now. */
  over: { result: MatchResult; items: { player: Item; rival: Item }; forfeit: boolean; elapsedMs: number };
  /** The opponent's connection dropped; they have a few seconds to come back. */
  opponentAway: { seconds: number };
  opponentBack: Record<string, never>;
  /** The opponent asked for a rematch. */
  rematchAsked: Record<string, never>;
}

/** Client → server. */
export interface ClientMessages {
  input: { action: Action };
  rematch: Record<string, never>;
}

/** Checks that something from the network is a well-formed action. */
export function parseAction(x: unknown): Action | null {
  if (!x || typeof x !== 'object') return null;
  const a = x as Record<string, unknown>;
  switch (a.kind) {
    case 'step':
    case 'emp':
    case 'bulkhead':
      return { kind: a.kind };
    case 'rotate':
      return (ROTATIONS as readonly unknown[]).includes(a.rotation) ? { kind: 'rotate', rotation: a.rotation as Rotation } : null;
    case 'swap':
      return (ITEMS as readonly unknown[]).includes(a.item) ? { kind: 'swap', item: a.item as Item } : null;
    default:
      return null;
  }
}

/** Rewrites agent ids in a value from seat ids to the receiver's "player"/"rival" view. */
export function relabel<T>(value: T, self: string, rival: string): T {
  return JSON.parse(JSON.stringify(value), (key, v) => {
    if ((key === 'agent' || key === 'id' || key === 'winner' || key === 'loser') && typeof v === 'string') {
      if (v === self) return SELF_ID;
      if (v === rival) return RIVAL_ID;
    }
    return v;
  }) as T;
}

/**
 * The events one player may hear about: their own, plus the shared moments (a cloak, a doorway
 * clash, the end of the match). A rival's seal is included only while it is in view.
 */
export function eventsFor(events: readonly MatchEvent[], self: string, view: AgentView): MatchEvent[] {
  return events.filter((e) => {
    switch (e.type) {
      case 'cloaked':
      case 'doorwayClash':
      case 'encounter':
      case 'coreClaimed':
      case 'matchOver':
        return true;
      case 'doorSealed':
        return e.agent === self || view.seals.some((s) => s.from === e.from && s.to === e.to);
      default:
        return e.agent === self;
    }
  });
}

/** Cosmetic look: a suit, a paint and the avatar behind the visor. Purely visual; never affects play. */
export const SUIT_IDS = ['vanguard', 'wraith', 'salvager'] as const;
export const PAINT_IDS = ['gunmetal', 'graphite', 'bone', 'rust', 'olive', 'navy', 'copper', 'sand', 'teal', 'violet'] as const;
export type SuitId = (typeof SUIT_IDS)[number];
export type PaintId = (typeof PAINT_IDS)[number];
export const AVATAR_IDS = ['kestrel', 'anvil', 'wren', 'ember', 'sage', 'brick', 'unit9', 'vega'] as const;
export type AvatarId = (typeof AVATAR_IDS)[number];
export interface ArmorChoice {
  suit: SuitId;
  paint: PaintId;
  avatar: AvatarId;
}
export const DEFAULT_ARMOR: ArmorChoice = { suit: 'vanguard', paint: 'gunmetal', avatar: 'kestrel' };

/** Checks an armor choice from the network, falling back to the default. */
export function parseArmor(x: unknown): ArmorChoice {
  const a = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>;
  const suit = (SUIT_IDS as readonly unknown[]).includes(a.suit) ? (a.suit as SuitId) : DEFAULT_ARMOR.suit;
  const paint = (PAINT_IDS as readonly unknown[]).includes(a.paint) ? (a.paint as PaintId) : DEFAULT_ARMOR.paint;
  const avatar = (AVATAR_IDS as readonly unknown[]).includes(a.avatar) ? (a.avatar as AvatarId) : DEFAULT_ARMOR.avatar;
  return { suit, paint, avatar };
}
